import { computed, reactive, ref, readonly, onMounted, onUnmounted } from 'vue';

const LS_SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const layoutConfig = reactive({
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    menuMode: 'static'
});

// Function to determine menu mode based on screen size
function getMenuModeForScreenSize() {
    return window.innerWidth <= 768 ? 'overlay' : 'static';
}

const THEME_KEY = 'corecry_theme';
let preferredTheme = null;
let systemTheme = null;
try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') preferredTheme = saved;
} catch { /* Storage is optional; the current tab still supports a manual choice. */ }
try {
    systemTheme = window.matchMedia?.('(prefers-color-scheme: dark)') || null;
} catch { /* Dark is the fallback when system theme detection is unavailable. */ }
const effectiveTheme = ref('dark');
const applyTheme = () => {
    effectiveTheme.value = preferredTheme || (systemTheme ? (systemTheme.matches ? 'dark' : 'light') : 'dark');
    const dark = effectiveTheme.value === 'dark';
    document.documentElement.classList.toggle('app-dark', dark);
    document.documentElement.style.colorScheme = effectiveTheme.value;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#212121' : '#F2F3F8');
};
const onSystemThemeChange = () => { if (!preferredTheme) applyTheme(); };
const onStoredThemeChange = (event) => {
    if (event.key !== THEME_KEY && event.key !== null) return;
    try { if (event.storageArea && event.storageArea !== localStorage) return; } catch { return; }
    preferredTheme = event.newValue === 'dark' || event.newValue === 'light' ? event.newValue : null;
    applyTheme();
};
const setTheme = (theme) => {
    if (theme !== 'dark' && theme !== 'light') return;
    preferredTheme = theme;
    applyTheme();
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* Keep the in-memory choice. */ }
};
const toggleTheme = () => setTheme(effectiveTheme.value === 'dark' ? 'light' : 'dark');
const isDarkTheme = computed(() => effectiveTheme.value === 'dark');
applyTheme();
if (systemTheme?.addEventListener) systemTheme.addEventListener('change', onSystemThemeChange);
else systemTheme?.addListener?.(onSystemThemeChange);
window.addEventListener('storage', onStoredThemeChange);
if (import.meta.hot) import.meta.hot.dispose(() => {
    if (systemTheme?.removeEventListener) systemTheme.removeEventListener('change', onSystemThemeChange);
    else systemTheme?.removeListener?.(onSystemThemeChange);
    window.removeEventListener('storage', onStoredThemeChange);
});

export function useTheme() {
    return { effectiveTheme: readonly(effectiveTheme), isDarkTheme, setTheme, toggleTheme };
}

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    activeMenuItem: null,
    sidebarCollapsed: false
});

// Initialize sidebar collapsed state from localStorage
try {
    const savedSidebarState = localStorage.getItem(LS_SIDEBAR_COLLAPSED_KEY);
    if (savedSidebarState === 'true') {
        layoutState.sidebarCollapsed = true;
    }
} catch {
    /* ignore */
}

// Initialize menu mode based on screen size
if (typeof window !== 'undefined') {
    layoutConfig.menuMode = getMenuModeForScreenSize();
    // Ensure menu is visible on large screens initially
    if (layoutConfig.menuMode === 'static') {
        layoutState.staticMenuDesktopInactive = false;
    }
}

export function useLayout() {
    const setActiveMenuItem = (item) => {
        layoutState.activeMenuItem = item.value || item;
    };

    // Handle responsive menu mode changes
    const handleResize = () => {
        const newMenuMode = getMenuModeForScreenSize();
        if (layoutConfig.menuMode !== newMenuMode) {
            layoutConfig.menuMode = newMenuMode;
            // Reset menu states when switching modes
            layoutState.overlayMenuActive = false;
            layoutState.staticMenuMobileActive = false;
            // Ensure menu is visible on large screens
            if (newMenuMode === 'static') {
                layoutState.staticMenuDesktopInactive = false;
            }
        }
    };

    // Set up resize listener
    onMounted(() => {
        window.addEventListener('resize', handleResize);
        // Ensure correct initial state
        handleResize();
        // Force menu to be visible on large screens
        if (window.innerWidth > 768) {
            layoutConfig.menuMode = 'static';
            layoutState.staticMenuDesktopInactive = false;
        }
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });

    const toggleMenu = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth > 768) {
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
            // Force sidebar to be expanded on mobile
            if (layoutState.staticMenuMobileActive) {
                layoutState.sidebarCollapsed = false;
            }
        }
    };

    const toggleSidebarCollapse = () => {
        layoutState.sidebarCollapsed = !layoutState.sidebarCollapsed;
        try {
            localStorage.setItem(LS_SIDEBAR_COLLAPSED_KEY, layoutState.sidebarCollapsed.toString());
        } catch {
            /* ignore */
        }
    };

    const isSidebarActive = computed(() => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive);

    const getPrimary = computed(() => layoutConfig.primary);

    const getSurface = computed(() => layoutConfig.surface);

    return {
        layoutConfig,
        layoutState,
        toggleMenu,
        toggleSidebarCollapse,
        isSidebarActive,
        getPrimary,
        getSurface,
        setActiveMenuItem
    };
}
